import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Plus, Trash2, Circle, CheckCircle2, ArrowRight, Star,
  MessageCircle, CalendarDays, Pen
} from 'lucide-react';
import { toast } from 'sonner';
import { DoodleSquiggle, DoodleStar } from './Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BULLET_TYPES = [
  { value: 'task', symbol: '\u2022', label: 'Task', color: '#1C1C1E' },
  { value: 'event', symbol: '\u25CB', label: 'Event', color: '#A2D2FF' },
  { value: 'note', symbol: '\u2013', label: 'Note', color: '#CDB4DB' },
  { value: 'inspiration', symbol: '\u2605', label: 'Inspiration', color: '#FFC8DD' },
];

export default function DailyLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({ bullet_type: 'task', content: '' });

  const fetchEntries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/journal?date=${date}`, { withCredentials: true });
      setEntries(data);
    } catch (e) {
      console.error('Failed to fetch journal:', e);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newEntry.content.trim()) return;
    try {
      await axios.post(`${API}/journal`, { ...newEntry, date }, { withCredentials: true });
      toast.success('Entry added!');
      setNewEntry({ bullet_type: 'task', content: '' });
      setShowAddForm(false);
      fetchEntries();
    } catch (e) {
      toast.error('Failed to add entry');
    }
  };

  const toggleComplete = async (entry) => {
    try {
      await axios.patch(`${API}/journal/${entry.id}?completed=${!entry.completed}`, {}, { withCredentials: true });
      fetchEntries();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const migrate = async (entry) => {
    try {
      await axios.patch(`${API}/journal/${entry.id}?migrated=true`, {}, { withCredentials: true });
      toast.success('Migrated to tomorrow!');
      fetchEntries();
    } catch (e) {
      toast.error('Failed to migrate');
    }
  };

  const deleteEntry = async (id) => {
    try {
      await axios.delete(`${API}/journal/${id}`, { withCredentials: true });
      fetchEntries();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const getBulletConfig = (type) => BULLET_TYPES.find(b => b.value === type) || BULLET_TYPES[0];

  const formatDate = (d) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const changeDate = (delta) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4" data-testid="daily-log">
      {/* Date Navigation */}
      <div className="flex items-center justify-between">
        <button onClick={() => changeDate(-1)} className="neo-btn px-3 py-2 bg-white" data-testid="prev-day">
          <ArrowRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="text-center">
          <h3 className="text-2xl font-bold font-['Fredoka']">{formatDate(date)}</h3>
          <div className="flex items-center justify-center gap-2 mt-1">
            <DoodleSquiggle width={40} color="#CDB4DB" />
            <CalendarDays className="w-4 h-4 text-gray-400" />
            <DoodleSquiggle width={40} color="#CDB4DB" />
          </div>
        </div>
        <button onClick={() => changeDate(1)} className="neo-btn px-3 py-2 bg-white" data-testid="next-day">
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Dot-grid paper background card */}
      <div className="neo-card p-6 relative overflow-hidden" style={{
        backgroundImage: 'radial-gradient(circle, #D0D0D0 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }}>
        {/* BuJo Legend */}
        <div className="flex flex-wrap gap-4 mb-4 pb-3 border-b-2 border-dashed border-gray-300">
          {BULLET_TYPES.map(bt => (
            <div key={bt.value} className="flex items-center gap-1.5 text-sm">
              <span className="text-lg font-bold" style={{ color: bt.color }}>{bt.symbol}</span>
              <span className="text-gray-600">{bt.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-lg font-bold text-green-600">&#10005;</span>
            <span className="text-gray-600">Done</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="text-lg font-bold text-blue-500">&#8250;</span>
            <span className="text-gray-600">Migrated</span>
          </div>
        </div>

        {/* Entries */}
        {loading ? (
          <p className="text-center text-gray-500 py-4">Loading...</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-8">
            <Pen className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-['Fredoka'] text-lg">Empty page... start writing!</p>
            <DoodleStar className="mx-auto mt-3" size={24} color="#FFC8DD" />
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => {
              const bt = getBulletConfig(entry.bullet_type);
              return (
                <div
                  key={entry.id}
                  className={`flex items-start gap-3 py-2 px-3 rounded-lg transition-all hover:bg-white/60 group ${
                    entry.completed ? 'opacity-60' : ''
                  } ${entry.migrated ? 'opacity-40' : ''}`}
                  data-testid={`journal-entry-${entry.id}`}
                >
                  {/* Bullet symbol / action */}
                  <button
                    onClick={() => entry.bullet_type === 'task' ? toggleComplete(entry) : null}
                    className="mt-0.5 text-xl font-bold leading-none min-w-[20px] text-center"
                    style={{ color: bt.color }}
                    data-testid={`journal-bullet-${entry.id}`}
                  >
                    {entry.completed ? <span className="text-green-600">&#10005;</span> 
                     : entry.migrated ? <span className="text-blue-500">&#8250;</span>
                     : bt.symbol}
                  </button>

                  {/* Content */}
                  <p className={`flex-1 text-lg font-['Nunito'] leading-snug ${
                    entry.completed ? 'line-through text-gray-400' : ''
                  } ${entry.migrated ? 'line-through text-gray-400 italic' : ''}`}>
                    {entry.content}
                  </p>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {entry.bullet_type === 'task' && !entry.completed && !entry.migrated && (
                      <button
                        onClick={() => migrate(entry)}
                        className="p-1 hover:bg-blue-100 rounded text-blue-500"
                        title="Migrate to tomorrow"
                        data-testid={`migrate-${entry.id}`}
                      >
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="p-1 hover:bg-red-100 rounded text-red-400"
                      data-testid={`delete-journal-${entry.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add Entry Form */}
        {showAddForm ? (
          <form onSubmit={handleAdd} className="mt-4 pt-3 border-t-2 border-dashed border-gray-300 space-y-3">
            <div className="flex gap-2">
              {BULLET_TYPES.map(bt => (
                <button
                  key={bt.value}
                  type="button"
                  onClick={() => setNewEntry({ ...newEntry, bullet_type: bt.value })}
                  className={`neo-btn px-3 py-1.5 text-sm ${
                    newEntry.bullet_type === bt.value ? 'bg-[#A2D2FF]' : 'bg-white'
                  }`}
                  data-testid={`bullet-type-${bt.value}`}
                >
                  <span className="mr-1">{bt.symbol}</span> {bt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newEntry.content}
                onChange={(e) => setNewEntry({ ...newEntry, content: e.target.value })}
                className="neo-input flex-1"
                placeholder="Write something..."
                autoFocus
                data-testid="journal-content-input"
              />
              <button type="submit" className="neo-btn px-4 py-2 bg-[#B9FBC0]" data-testid="save-journal-btn">
                Add
              </button>
              <button type="button" onClick={() => setShowAddForm(false)} className="neo-btn px-4 py-2 bg-gray-200" data-testid="cancel-journal-btn">
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="mt-4 neo-btn w-full py-3 bg-white/80 border-dashed flex items-center justify-center gap-2"
            data-testid="add-journal-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Add Entry</span>
          </button>
        )}
      </div>
    </div>
  );
}
